<script setup lang="ts">
import type { IChipVariant } from "../ui/Chip/chip.interface"
import type { EventVault } from "@/models"
import type { IMenuItem } from "@/types"
import cover from "@brand-cover"
import { computed } from "vue"
import { FreemiumUpgradeCard } from "@/components"
import { AppLink, ButtonLink, Chip, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus, ExperienceType, ProductTypes } from "@/models"
import { convertDateTimezone } from "@/utils"

const props = withDefaults(
  defineProps<{
    eventVault?: EventVault
    isLoading?: boolean
  }>(),
  {
    isLoading: false,
  },
)

const emit = defineEmits<{
  (e: "showLinks", eventVaultId?: number): void
  (e: "showExport", eventVaultId?: number): void
  (e: "showExtend", eventVaultId?: number): void
  (e: "showDelete", eventVaultId?: number): void
}>()

const { t, d } = useTranslation()

const packageLabel = computed<string>(() =>
  ProductTypes.getLabel(
    props.eventVault?.productKey,
    props.eventVault?.productName,
  ),
)

const statusLabel = computed<string>(() =>
  EventVaultStatus.getLabel(props.eventVault?.status ?? EventVaultStatus.Draft),
)

const canViewActions = computed<boolean>(
  () => props.eventVault?.status !== EventVaultStatus.Archived,
)

const isSubscriptionEvent = computed(() =>
  Boolean(props.eventVault?.packageSubscriptionId),
)

const isFreemiumEvent = computed(() => Boolean(props.eventVault?.freemiumMode))

const quickLinks = computed(() => {
  const eventVaultId = props.eventVault?.id

  const isEventSinglePage = props.eventVault?.eventVaultSettings?.experienceType === ExperienceType.SinglePage

  const links: Record<string, IMenuItem> = {
    view_links: {
      key: "view_links",
      label: t("host_event_card.quick_links.view_links"),
      onClick: () => emit("showLinks", eventVaultId),
    },
    view_gallery: {
      key: "view_gallery",
      label: t("host_event_card.quick_links.view_gallery"),
      to: isEventSinglePage ? `/guest/${eventVaultId}` : `/guest/${eventVaultId}/gallery`,
    },
    upgrade: {
      key: "upgrade",
      label: t("host_event_card.quick_links.upgrade"),
    },
    download_zip: {
      key: "download_zip",
      label: t("host_event_card.quick_links.download_zip"),
      onClick: () => emit("showExport", eventVaultId),
    },
    extend: { key: "extend", label: t("host_event_card.quick_links.extend") },
    delete: { key: "delete", label: t("host_event_card.quick_links.delete") },
    manage_subscription: {
      key: "manage_subscription",
      label: t("host_event_card.quick_links.manage_subscription"),
    },
  }

  if (isSubscriptionEvent.value) {
    links.manage_subscription.href
      = "https://guestpix.com/my-account/subscriptions/"
  }

  if (!isSubscriptionEvent.value) {
    links.delete.onClick = () => emit("showDelete", eventVaultId)

    if (!props.eventVault?.freemiumMode) {
      links.upgrade.to = `/event/${eventVaultId}/upgrade`
      links.upgrade.href = undefined
      links.extend.onClick = () => emit("showExtend", eventVaultId)
    }
  }

  return links
})

const statusData = computed(() => {
  const defaultLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.upgrade,
  ]
  const closedLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.download_zip,
  ]
  const expiringLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.extend,
    quickLinks.value.manage_subscription,
  ]
  const freemiumExpiringLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.delete,
  ]
  const expiredLinks = [
    quickLinks.value.view_gallery,
    quickLinks.value.extend,
    quickLinks.value.delete,
    quickLinks.value.manage_subscription,
  ]
  const freemiumExpiredLinks = [
    quickLinks.value.view_gallery,
    quickLinks.value.delete,
    quickLinks.value.upgrade,
  ]
  const extendInProgressLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
  ]
  const deletingInProgressLinks = [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
  ]

  return {
    [EventVaultStatus.Draft]: {
      chipVariant: "info" as IChipVariant,
      links: defaultLinks,
    },
    [EventVaultStatus.Ready]: {
      chipVariant: "info" as IChipVariant,
      links: defaultLinks,
    },
    [EventVaultStatus.Live]: {
      chipVariant: "success" as IChipVariant,
      links: defaultLinks,
    },
    [EventVaultStatus.Closed]: {
      chipVariant: "info" as IChipVariant,
      links: closedLinks,
    },
    [EventVaultStatus.Expiring]: {
      chipVariant: "warning" as IChipVariant,
      links: isFreemiumEvent.value ? freemiumExpiringLinks : expiringLinks,
    },
    [EventVaultStatus.Expired]: {
      chipVariant: "danger" as IChipVariant,
      links: isFreemiumEvent.value ? freemiumExpiredLinks : expiredLinks,
    },
    [EventVaultStatus.ExtendInProgress]: {
      chipVariant: "warning" as IChipVariant,
      links: extendInProgressLinks,
    },
    [EventVaultStatus.DeletingInProgress]: {
      chipVariant: "danger" as IChipVariant,
      links: deletingInProgressLinks,
    },
    [EventVaultStatus.Archived]: { chipVariant: "info" as IChipVariant },
    [EventVaultStatus.All]: { chipVariant: "info" as IChipVariant },
  } as Record<
    EventVaultStatus,
    { chipVariant: IChipVariant, links?: IMenuItem[] }
  >
})

const currentStatusChipVariant = computed<IChipVariant>(() => {
  const status = props.eventVault?.status ?? EventVaultStatus.Draft
  return statusData.value[status]?.chipVariant ?? "info"
})

const currentStatusLinks = computed<IMenuItem[] | undefined>(() => {
  const status = props.eventVault?.status
  if (!status)
    return undefined
  return statusData.value[status]?.links
})
</script>

<template>
  <div v-if="isLoading">
    <div
      class="border border-tertiary bg-white rounded-lg overflow-hidden w-full"
    >
      <div
        class="flex flex-col md:flex-row justify-between items-center gap-4 bg-tertiary p-4"
      >
        <div class="h-10 w-1/4 gpx-skeleton hidden md:block" />
        <div class="h-10 gpx-skeleton w-full md:w-1/4" />
      </div>
      <div class="rounded-lg h-40 m-4 gpx-skeleton" />
      <div
        class="flex flex-col md:flex-row items-center gap-4 p-4 text-sm justify-center bg-tertiary"
      >
        <div class="h-6 w-full md:w-1/2 gpx-skeleton" />
      </div>
    </div>
  </div>

  <div
    v-if="!isLoading && eventVault"
    class="border border-tertiary bg-white rounded-lg overflow-hidden font-montserrat w-full"
  >
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-tertiary p-4"
    >
      <AppLink :to="`/event/${eventVault.id}/`">
        <h3
          class="text-md md:text-lg font-bold font-comfortaa text-secondary-card-text"
        >
          {{ eventVault.title }}
        </h3>
      </AppLink>

      <div class="flex items-center gap-2 sm:flex-row flex-col">
        <FreemiumUpgradeCard
          v-if="eventVault.freemiumMode && eventVault.upgradeUrl"
          variant="mini"
          class="w-full sm:w-auto"
          :upgrade-url="eventVault.upgradeUrl"
        />

        <ButtonLink
          v-if="canViewActions"
          :to="`/event/${eventVault.id}/`"
          class="w-full sm:w-auto"
        >
          <IconLucideLayoutDashboard />
          {{ t("host_event_card.view_dashboard") }}
        </ButtonLink>
      </div>
    </div>

    <div class="p-4 flex flex-col md:flex-row gap-4">
      <div>
        <AppLink
          :to="`/event/${eventVault.id}/`"
          class="hover:underline focus:underline"
        >
          <img
            v-if="eventVault.coverImageUrl"
            :src="eventVault.coverImageUrl"
            :alt="t('cover_image_alt')"
            class="object-cover rounded-lg h-40 w-full md:w-64"
            loading="lazy"
          >
          <img
            v-else
            :src="cover"
            :alt="t('cover_image_alt')"
            class="object-cover rounded-lg h-40 w-full md:w-64"
            loading="lazy"
          >
        </AppLink>
      </div>

      <div class="flex flex-col sm:flex-row justify-between gap-4 grow">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 self-start mb-2">
            <Icon icon="Calendar" />
            <span>{{
              eventVault?.timezone
                ? d(
                  convertDateTimezone(
                    eventVault.eventDate ?? new Date(),
                    eventVault.timezone,
                  ),
                  "long",
                )
                : d(eventVault.eventDate ?? new Date(), "short")
            }}</span>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <Chip
              :label="statusLabel"
              :variant="currentStatusChipVariant"
              size="sm"
            />
            <Chip :label="packageLabel" variant="secondary" size="sm" />
          </div>
          <p
            v-if="!isSubscriptionEvent"
            class="text-sm"
            data-test="expiry-date"
          >
            {{
              t("host_event_card.expiry", {
                expiry: eventVault.expiryDate
                  ? d(eventVault.expiryDate, "short")
                  : "N/A",
              })
            }}
          </p>
        </div>

        <div v-if="canViewActions && currentStatusLinks?.length">
          <h4 class="font-semibold">
            {{ t("host_event_card.quick_links.title") }}
          </h4>
          <ul class="text-link">
            <li
              v-for="link in currentStatusLinks"
              :key="link.key"
              :data-test="`quick_link_${link.key}`"
            >
              <AppLink
                v-if="link.to"
                :to="link.to"
                class="underline hover:text-link-hover focus:text-link-hover"
              >
                {{ link.label }}
              </AppLink>
              <button
                v-else-if="link.onClick"
                class="underline hover:text-link-hover focus:text-link-hover text-left cursor-pointer"
                @click.prevent="link.onClick"
              >
                {{ link.label }}
              </button>
              <a
                v-else-if="link.href"
                :href="link.href"
                class="underline hover:text-link-hover focus:text-link-hover text-left"
                target="_blank"
              >
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      class="flex flex-col sm:flex-row flex-wrap items-center gap-4 p-4 text-sm justify-center bg-tertiary"
    >
      <div class="flex gap-4">
        <div class="flex items-center gap-2" data-test="total_guests">
          <Icon icon="Users" size="sm" class="text-nav-active" />
          {{
            t("host_event_card.stats.guests", {
              count: eventVault.totalGuests ?? 0,
            })
          }}
        </div>
        <div class="flex items-center gap-2" data-test="total_photos">
          <Icon icon="Images" size="sm" class="text-nav-active" />
          {{
            t("host_event_card.stats.photos", {
              count: eventVault.totalPics ?? 0,
            })
          }}
        </div>
      </div>
      <div class="flex gap-4">
        <div class="flex items-center gap-2" data-test="total_messages">
          <Icon icon="MessageCircle" size="sm" class="text-nav-active" />
          {{
            t("host_event_card.stats.messages", {
              count: eventVault.totalMessages ?? 0,
            })
          }}
        </div>
        <div
          v-if="eventVault.eventVaultSettings?.albumsEnabled"
          class="flex items-center gap-2"
          data-test="total_albums"
        >
          <Icon icon="ImageBook" size="sm" class="text-nav-active" />
          {{
            t("host_event_card.stats.albums", {
              count: eventVault.totalAlbums ?? 0,
            })
          }}
        </div>
      </div>
    </div>
  </div>
</template>
